import { App, Route, UniversalAppState } from "@pimo/pimo-app-builder";
import { DefaultOverlayPartialAppState, PMPopUp } from "@pimo/pimo-components";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import { ResourceLockPlugin } from "@pimo/resource-lock-plugin";
import { BasicItem } from "product-test-types";
import {
  CHARACTER_LIMITATIONS,
  getFieldValidation,
  validateBasicItemForm,
} from "product-test-utils";
import { generatePath } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { TestAppState } from "../app";
import {
  getFormValuesFromEventPayload,
  isCustomButtonEventPayload,
  PimoFormDialog,
} from "../components/pimo-form";
import { APP_ROUTES } from "../constants";
import { createBasicItem, updateBasicItem } from "../helpers/fetch-basic-items";
import { fetchEntityConfiguration } from "../helpers/fetch-entity-configurations";
import { fetchOverlay } from "../helpers/fetch-overlay";
import { fetchOverview } from "../helpers/fetch-overview";
import { fetchProject } from "../helpers/fetch-project";
import { fetchSettings, fetchSetupSteps } from "../helpers/fetch-settings";
import { showSnackbar } from "../helpers/show-snackbar";
import { toDropdownItems } from "../utils/dropdown-options";
import {
  getCreateEntityLabel,
  getEditEntityLabel,
} from "../utils/get-add-new-label";
import { getTotalAndCompletedSteps } from "../utils/get-total-and-completed-steps";

const STYLE_TEXT = {
  "& .MuiInputBase-input": {
    fontSize: "14px",
  },
  // multiline textarea
  "& .MuiInputBase-inputMultiline, & textarea": {
    fontSize: "14px",
  },
  // label size (optional)
  "& .MuiInputLabel-root": {
    fontSize: "14px",
  },
  fontSize: "14px",
};

export async function buildTrackingItemFormRoute(
  app: App<TestAppState>,
  parentRoute: Route<"id" | "viewname", TestAppState>
) {
  const dialog = app.createView({
    name: "add-tracking-item",
  });
  let projectId: string | undefined;
  let trackingItemId: string | undefined;
  const editForm = new PimoFormDialog<BasicItem>();

  const form = dialog.addComponent({ component: editForm });

  registerFormEvents();

  function registerFormEvents() {
    const language = app.getAppState().language || "en";
    form.on("submit", async ({ payload }) => {
      if (isCustomButtonEventPayload(payload) && !payload.hasChanges) {
        closeEditForm();
        return;
      }

      const submittedTrackingItem =
        getFormValuesFromEventPayload<BasicItem>(payload);
      if (!submittedTrackingItem) {
        return;
      }

      if (submittedTrackingItem.flag === "create") {
        await createBasicItem(
          submittedTrackingItem,
          app.getAppState().currentReport?.project?.id
        );
      } else {
        await updateBasicItem(
          submittedTrackingItem.id,
          submittedTrackingItem,
          app.getAppState().currentReport?.project?.id
        );
      }
      const [overlay, overview, currentReport, completedSetupSteps, settings] =
        await Promise.all([
          fetchOverlay(),
          fetchOverview(),
          fetchProject({
            id: app.getAppState().currentReport?.project?.id ?? "",
          }),
          fetchSetupSteps(),
          fetchSettings(),
        ]);

      const { totalSteps, completedSteps } =
        getTotalAndCompletedSteps(completedSetupSteps);

      app?.patchAppState({
        overlay,
        overview,
        currentReport,

        welcomeScreenStatus: {
          steps: completedSetupSteps,
          totalSteps,
          completedSteps,
        },
        settings: { ...app.getAppState().settings, ...settings },
      });

      showSnackbar(
        app as unknown as App<
          UniversalAppState & DefaultOverlayPartialAppState
        >,
        getLocalizedMessage(language, "allChangesSaved") ||
          "All changes have been saved.",
        "success"
      );

      closeEditForm();
    });

    form.on("cancel", () => closeEditForm());
    form.on("cancel-with-changes", () =>
      app.patchAppState({ showEditFormConfirmationDialog: true })
    );
    form.on("close", () => closeEditForm());
  }
  function closeEditForm() {
    if (trackingItemId) {
      const resourceLockPlugin =
        app.getPluginByName<ResourceLockPlugin<TestAppState>>(
          "ResourceLockPlugin"
        );

      resourceLockPlugin?.unlock(`item-${trackingItemId}`);
    }

    app.patchAppState({
      editForm: {
        currentTrackingItem: undefined,
      },
    });
    projectId &&
      app.navigate(
        generatePath(APP_ROUTES.report, {
          id: projectId,
          viewname: "dashboard",
        })
      );
  }

  function createConfirmationDialog() {
    const confirmationDialog = dialog.addComponent({
      component: PMPopUp,
    });

    confirmationDialog.mapVisibility(
      ({ showEditFormConfirmationDialog }) =>
        showEditFormConfirmationDialog === true
    );

    confirmationDialog.mapState(() => {
      const language = app.getAppState().language || "en";
      return {
        title:
          getLocalizedMessage(language, "dialog.title.unsavedChanges", {
            entity: "form",
          }) || "Unsaved Changes",
        description:
          getLocalizedMessage(language, "dialog.message.unsavedChanges", {
            entity: "form",
          }) ||
          "You have made changes that have not been saved. If you close the settings, these changes will be lost.",
        maxWidth: "sm",

        cancelButtonText:
          getLocalizedMessage(language, "dialog.button.cancel") || "Cancel",
        cancelButtonColor: "white",
        confirmationButtonColor: "blue",
        confirmationButtonText:
          getLocalizedMessage(language, "dialog.button.continue") || "Continue",

        cancelButtonFireEvent: () => closeConfirmationDialog(),
        confirmationButtonFireEvent: () => {
          closeConfirmationDialog();
          closeEditForm();
        },
        onClose: () => closeConfirmationDialog(),
        slotProps: { backdrop: { sx: { backgroundColor: "transparent" } } },
      };
    });

    function closeConfirmationDialog() {
      app.patchAppState({
        showEditFormConfirmationDialog: false,
      });
    }
  }

  const route = parentRoute.createChildRoute<"tracking-item-id">({
    path: APP_ROUTES.trackingItemDialog,
    view: dialog,
    isOverlayChild: true,
  });

  route.on("load", async ({ payload }) => {
    projectId = window.location.pathname.split("/")[2];
    trackingItemId = payload?.parameters?.["tracking-item-id"];

    const currentProject =
      projectId && projectId !== "new-project"
        ? (await fetchProject({ id: projectId }))?.project
        : undefined;

    const configuration = await fetchEntityConfiguration();
    const oldSettings = app.getAppState().settings;
    app.patchAppState({
      editForm: {
        currentProject: currentProject,
        currentTrackingItem: currentProject?.reportingItems
          ?.flatMap((i) => i.basicItem)
          .find(
            (item) =>
              String(item?.id) ===
              String(payload?.parameters?.["tracking-item-id"])
          ),
      },
      settings: {
        ...oldSettings,
        configuration: configuration,
      },
    });

    form.mapState((appState) => {
      const language = appState.language || "en";
      const itemNameKey = appState.settings?.configuration?.itemName;

      return {
        ...{
          ...(appState.editForm?.currentTrackingItem ?? {
            id: uuidv4(),
            flag: "create",
            projectSpecificItem: true,
            startDate: new Date().toISOString().split("T")[0],
            endDate: new Date().toISOString().split("T")[0],
          }),
        },
        ...{
          validateFormData: (data: Omit<BasicItem, "id">) =>
            validateBasicItemForm(data, configuration, {
              fieldRequired:
                getLocalizedMessage(
                  language,
                  "validationErrors.fieldRequired"
                ) || "This field is required",
              dateNotValid:
                getLocalizedMessage(
                  language,
                  "validationErrors.dateNotValid"
                ) || "This date is not valid",
              endDateAfterStartDate:
                getLocalizedMessage(
                  language,
                  "validationErrors.endDateAfterStartDate"
                ) || "End date should be after start date",
            }),
        },

        title: appState.editForm?.currentTrackingItem
          ? getEditEntityLabel(language, itemNameKey, "item")
          : getCreateEntityLabel(language, itemNameKey, "item"),
        inline: true,
        enableCloseButton: true,
        customButtons: {
          left: [],
          right: [
            {
              id: "cancel",
              label:
                getLocalizedMessage(appState.language || "en", "cancel") ||
                "Cancel",
              eventName: "cancel",
              includeFormDataInPayload: true,
            },
            {
              id: "save-and-close",
              label:
                getLocalizedMessage(
                  appState.language || "en",
                  "saveAndClose"
                ) || "Save & Close",
              eventName: "submit",
              sx: { color: "white" },
              variant: "contained",
              validateBeforeEmit: true,
              includeFormDataInPayload: true,
            },
          ],
        },
      };
    });
    await editForm.buildDynamicTabsAsynchronously(async (editForm) => {
      const appState = app.getAppState();
      const configuration = appState.settings?.configuration;
      const language = appState.language;
      const overallTab = editForm.addFormTab({
        label: getLocalizedMessage(language, "summary") || "Summary",
        id: "summary",
      });

      overallTab.addFormField({
        type: "text",
        label: `${getLocalizedMessage(language, "name") || "Name"}${
          getFieldValidation(configuration, "name", "itemBasic") ? "*" : ""
        }`,
        maxLength: {
          value: 100,
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.maxLength",
              { maxLength: CHARACTER_LIMITATIONS["itemName"] }
            ) ||
            `Must be ${CHARACTER_LIMITATIONS["itemName"]} characters or less`,
        },
        required: {
          value: true,
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.fieldRequired"
            ) || "This field is required",
        },
        multiline: false,
        name: "name",
        sx: STYLE_TEXT,
        uiOptions: {
          fieldSize: { xs: 6 },
        },
      });

      overallTab.addFormField({
        type: "text",
        label: `${getLocalizedMessage(language, "customId") || "Custom ID"}${
          getFieldValidation(configuration, "customId", "itemBasic") ? "*" : ""
        }`,
        multiline: false,
        name: "customId",
        maxLength: {
          value: CHARACTER_LIMITATIONS["itemCustomId"],
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.maxLength",
              { maxLength: CHARACTER_LIMITATIONS["itemCustomId"] }
            ) ||
            `Must be ${CHARACTER_LIMITATIONS["itemCustomId"]} characters or less`,
        },
        required: {
          value: true,
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.fieldRequired"
            ) || "This field is required",
        },
        sx: STYLE_TEXT,
        uiOptions: {
          fieldSize: { xs: 6 },
        },
      });

      overallTab.addFormField({
        type: "rich-text",
        label: `${
          getLocalizedMessage(language, "description") || "Description"
        }${
          getFieldValidation(configuration, "description", "itemBasic")
            ? "*"
            : ""
        }`,
        multiline: true,
        name: "description",
        maxLength: {
          value: 100,
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.maxLength",
              { maxLength: CHARACTER_LIMITATIONS["itemDescription"] }
            ) ||
            `Must be ${CHARACTER_LIMITATIONS["itemDescription"]} characters or less`,
        },
        required: {
          value: true,
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.fieldRequired"
            ) || "This field is required",
        },
        minRows: 3,
        sx: STYLE_TEXT,

        isVisible: () =>
          Boolean(
            configuration?.fieldsDefinition
              ?.filter((field) => field.purpose === "itemBasic")
              ?.find((f) => f.fieldName === "description")?.enabled
          ),
      });

      overallTab.addFormField({
        type: "text",
        label: `${getLocalizedMessage(language, "owner") || "Owner"}${
          getFieldValidation(configuration, "owner", "itemBasic") ? "*" : ""
        }`,
        multiline: false,
        name: "owner",
        maxLength: {
          value: CHARACTER_LIMITATIONS["itemOwner"],
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.maxLength",
              { maxLength: CHARACTER_LIMITATIONS["itemOwner"] }
            ) ||
            `Must be ${CHARACTER_LIMITATIONS["itemOwner"]} characters or less`,
        },
        required: {
          value: true,
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.fieldRequired"
            ) || "This field is required",
        },
        sx: STYLE_TEXT,
        uiOptions: {
          fieldSize: { xs: 6 },
        },
        isVisible: () =>
          Boolean(
            configuration?.fieldsDefinition
              ?.filter((field) => field.purpose === "itemBasic")
              ?.find((f) => f.fieldName === "owner")?.enabled
          ),
      });

      overallTab.addFormField({
        type: "date",
        label: `${getLocalizedMessage(language, "startDate") || "Start Date"}${
          getFieldValidation(configuration, "startDate", "itemBasic") ? "*" : ""
        }`,
        name: "startDate",
        uiOptions: {
          fieldSize: { xs: 6 },
        },
        isVisible: () =>
          Boolean(
            configuration?.fieldsDefinition
              ?.filter((field) => field.purpose === "itemBasic")
              ?.find((f) => f.fieldName === "startDate")?.enabled
          ),
      });
      overallTab.addFormField({
        type: "date",
        label: `${getLocalizedMessage(language, "endDate") || "End Date"}${
          getFieldValidation(configuration, "endDate", "itemBasic") ? "*" : ""
        }`,
        name: "endDate",
        uiOptions: {
          fieldSize: { xs: 6 },
        },
        isVisible: () =>
          Boolean(
            configuration?.fieldsDefinition
              ?.filter((field) => field.purpose === "itemBasic")
              ?.find((f) => f.fieldName === "endDate")?.enabled
          ),
      });

      overallTab.addFormField({
        type: "dropdown",
        label: `${
          configuration?.fieldsDefinition
            ?.filter((field) => field.purpose === "itemBasic")
            ?.find(
              (f) =>
                f.fieldName === "dropdownFieldOne" &&
                f.enabled === true &&
                (configuration?.categoryDefinition?.length ?? 0) > 0
            )?.label ??
          (getLocalizedMessage(language, "category") || "Category")
        }${
          getFieldValidation(configuration, "dropdownFieldOne", "itemBasic")
            ? "*"
            : ""
        }`,
        name: `dropdownFieldOne`,
        sx: STYLE_TEXT,
        menuItemsSx: STYLE_TEXT,
        isVisible: () =>
          Boolean(
            configuration?.fieldsDefinition
              ?.filter((field) => field.purpose === "itemBasic")
              ?.find((f) => f.fieldName === "dropdownFieldOne")?.enabled
          ),
        uiOptions: {
          fieldSize: { xs: 6 },
        },
        items: toDropdownItems(configuration?.dropdownFieldOneDefinition),
      });

      overallTab.addFormField({
        type: "dropdown",
        label: `${
          configuration?.fieldsDefinition
            ?.filter((field) => field.purpose === "itemBasic")
            ?.find((f) => f.fieldName === "maturity")?.label ?? "Maturity"
        }${
          getFieldValidation(configuration, "maturity", "itemBasic") ? "*" : ""
        }`,
        name: "maturity",
        sx: STYLE_TEXT,
        menuItemsSx: STYLE_TEXT,
        required: getFieldValidation(configuration, "maturity", "itemBasic")
          ? {
              value: true,
              message:
                getLocalizedMessage(
                  language || "en",
                  "validationErrors.fieldRequired"
                ) || "This field is required",
            }
          : undefined,
        isVisible: () =>
          Boolean(
            configuration?.fieldsDefinition
              ?.filter((field) => field.purpose === "itemBasic")
              ?.find((f) => f.fieldName === "maturity")?.enabled
          ),
        uiOptions: {
          fieldSize: { xs: 6 },
        },
        items:
          configuration?.maturityConfiguration?.map((entry) => ({
            value: entry.name,
            label: entry.name,
          })) ?? [],
      });

      overallTab.addFormField({
        type: "dropdown",
        label: `${
          configuration?.fieldsDefinition
            ?.filter((field) => field.purpose === "itemBasic")
            ?.find((f) => f.fieldName === "dimension")?.label ?? "Dimension"
        }${
          getFieldValidation(configuration, "dimension", "itemBasic") ? "*" : ""
        }`,
        name: "dimension",
        sx: STYLE_TEXT,
        menuItemsSx: STYLE_TEXT,
        required: getFieldValidation(configuration, "dimension", "itemBasic")
          ? {
              value: true,
              message:
                getLocalizedMessage(
                  language || "en",
                  "validationErrors.fieldRequired"
                ) || "This field is required",
            }
          : undefined,
        isVisible: () =>
          Boolean(
            configuration?.fieldsDefinition
              ?.filter((field) => field.purpose === "itemBasic")
              ?.find((f) => f.fieldName === "dimension")?.enabled
          ),
        uiOptions: {
          fieldSize: { xs: 6 },
        },
        items:
          configuration?.dimensionDefinition?.map((entry) => ({
            value: entry.name,
            label: entry.name,
          })) ?? [],
      });
    });
    createConfirmationDialog();
  });
}
